import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/** The discount window's notes in storage, the same keys assets/promo-popup.js keeps. */
const scopedKey = (name) => {
  const theme = window.Shopify?.theme?.id;
  return theme ? `atelier:${theme}:${name}` : null;
};

/**
 * The discount window's offer in the cart, after Treado Multi's coupon in its cart drawer.
 *
 * It stands for one visitor only: the one who closed the window without taking the code,
 * and whose cart does not have it already. The server marks that with data-code-in-cart:
 * the cart outlasts this browser's note. Pressing it opens the window again, with its form.
 * On the page Shopify returns after a successful sign-up it shows the code instead, as
 * Treado's does; the window has put the code on the cart by then, and from then on the
 * cart's discount field shows it applied. Should Shopify hold the code on the cart without
 * applying it before checkout, where it learns who is buying, the offer shows the code as
 * added and says when it will apply.
 *
 * The cart drawer and the cart page re-render this markup whenever the cart changes, and
 * the server always renders it hidden. Morph hands updatedCallback() to the node it parsed,
 * not to this one, so the offer watches its own subtree and sets its state again after
 * every render, before the next paint.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} offer - The offer, a button that opens the window.
 * @property {HTMLElement} done - The code, after a successful sign-up.
 * @property {HTMLElement} code - Where the code is written.
 * @property {HTMLElement} pending - The code added but not applied before checkout.
 * @property {HTMLElement} pendingCode - Where that code is written.
 *
 * @extends {Component<Refs>}
 */
class PromoOfferComponent extends Component {
  requiredRefs = ['offer', 'done', 'code', 'pending', 'pendingCode'];

  /** The window's code as the cart holds it, once known: { code, applicable }. */
  held = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('promo-popup:state', this.sync);
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.onCartUpdate);
    this.watcher = new MutationObserver(() => this.updatedCallback());
    this.watcher.observe(this, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden', 'data-code-in-cart'],
    });
    this.sync();
    this.checkCart();
  }

  updatedCallback() {
    super.updatedCallback();
    this.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('promo-popup:state', this.sync);
    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.onCartUpdate);
    this.watcher?.disconnect();
  }

  get popup() {
    return document.querySelector('promo-popup');
  }

  read(name) {
    const key = scopedKey(name);
    if (!key) return '';
    for (const store of [window.localStorage, window.sessionStorage]) {
      try {
        const kept = store.getItem(key);
        if (kept) return kept;
      } catch {
        // Denied or absent storage answers nothing.
      }
    }
    return '';
  }

  /**
   * Asks the cart how it holds the window's code: only for a visitor who took it, and only
   * when the cart has something in it. The answer arrives after the first paint.
   */
  async checkCart() {
    const code = this.read('promo-code');
    if (!code || this.read('promo') !== 'done') return;
    try {
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();
      const held = (cart.discount_codes || []).find(
        (/** @type {{ code: string }} */ one) => one.code.toLowerCase() === code.toLowerCase()
      );
      this.held = held && cart.item_count > 0 ? { code: held.code, applicable: held.applicable } : null;
    } catch {
      this.held = null;
    }
    this.sync();
  }

  /* The cart's events arrive once the cart has changed (assets/events.js). */
  onCartUpdate = () => {
    this.checkCart();
  };

  /**
   * The code shown after a sign-up is the one the window printed: Shopify prints it only on
   * the page it renders after accepting an address. Without the window on the page the
   * offer has nothing to open.
   */
  sync = () => {
    const popup = this.popup;
    const subscribed = popup?.querySelector('[data-promo-subscribed]');
    const code = popup?.querySelector('[data-promo-code]')?.textContent.trim() || '';

    const inCart = this.hasAttribute('data-code-in-cart') || Boolean(this.held);

    let view = '';
    if (subscribed && code) view = 'done';
    else if (popup && this.read('promo') === 'closed' && !inCart) view = 'offer';
    else if (this.held && !this.held.applicable) view = 'pending';

    /* Only what differs is written, so the offer's own writes settle its watcher. */
    const hide = (/** @type {HTMLElement} */ element, hidden) => {
      if (element.hidden !== hidden) element.hidden = hidden;
    };
    const write = (/** @type {HTMLElement} */ element, text) => {
      if (element.textContent !== text) element.textContent = text;
    };
    hide(this.refs.offer, view !== 'offer');
    hide(this.refs.done, view !== 'done');
    hide(this.refs.pending, view !== 'pending');
    if (view === 'done') write(this.refs.code, code);
    if (view === 'pending' && this.held) write(this.refs.pendingCode, this.held.code);
    hide(this, view === '');
  };

  /**
   * Opens the window with its form. From the cart drawer the window opens modal: the drawer
   * is a modal dialog, so a window that was not modal would stand under it and never get the
   * focus.
   */
  open() {
    this.popup?.reopen?.({ modal: Boolean(this.closest('dialog[open]')) });
  }
}

if (!customElements.get('promo-offer-component')) {
  customElements.define('promo-offer-component', PromoOfferComponent);
}
